import { Pager, PAGE_SIZE, todayIn, useScrollReset } from '@pa/ui'
import { Bug } from 'lucide-react'
import type { ReactNode } from 'react'
import type { ErrorsAdapter } from '../hooks/adapters'
import { toErrorListParams, withFilter, type ErrorsSearch } from '../model/search'
import { ErrorCounters } from './ErrorCounters'
import { ErrorDrawer } from './ErrorDrawer'
import { ErrorFilters } from './ErrorFilters'
import { ErrorTable } from './ErrorTable'

/** UserFlows C-03's purpose copy. */
export const ERRORS_PURPOSE =
  'Failures in online booking, for the dev team. Patients only see a “please call us” message.'

export interface ErrorsPageProps {
  adapter: ErrorsAdapter
  /** Presets and times: the clinic's zone (C-03, S-04 tab) or the viewer's (S-08). */
  timeZone: string
  search: ErrorsSearch
  onSearch: (next: ErrorsSearch, opts?: { replace?: boolean }) => void
  /** `page` (C-03, S-08: the page head) or `tab` (inside S-04). */
  layout?: 'page' | 'tab'
  /** S-08: the Clinic filter's options (#38). */
  clinics?: readonly { id: string; name: string }[] | undefined
  /** S-08: the drawer's link to the clinic's S-04 Errors tab. */
  renderClinicLink?: (clinic: { id: string; name: string }) => ReactNode
}

/**
 * The shared `errors` feature (Frontend §5.4): C-03 / C-03a in the clinic app; S-08 and the S-04 Errors tab in the
 * admin app. Counters (24 h / 7 days, filters ignored), the filters (all in the URL), the table, the numbered pager
 * (100 per page, D-28), loading / empty / error-with-Retry states, and the drawer (`?error=`).
 */
export function ErrorsPage({
  adapter,
  timeZone,
  search,
  onSearch,
  layout = 'page',
  clinics,
  renderClinicLink,
}: ErrorsPageProps) {
  const today = todayIn(timeZone)
  const params = toErrorListParams(search, timeZone, today)
  const list = adapter.useList(params)
  const summary = adapter.useSummary()
  const rowsRef = useScrollReset<HTMLDivElement>([search.page, params.from, params.to, params.code, params.step])
  const page = list.data
  const items = page?.items ?? []
  const set = (patch: Partial<ErrorsSearch>) => {
    onSearch(withFilter(search, patch))
  }

  return (
    <>
      {layout === 'page' ? (
        <div className="page-head">
          <div>
            <h1>Errors</h1>
            <p>{ERRORS_PURPOSE}</p>
          </div>
          <ErrorCounters summary={summary.data} />
        </div>
      ) : (
        <div className="row err-tab-head">
          <p className="muted">{ERRORS_PURPOSE}</p>
          <ErrorCounters summary={summary.data} />
        </div>
      )}

      <div className="card card-fill">
        <ErrorFilters
          search={search}
          summary={summary.data}
          today={today}
          clinics={adapter.crossClinic ? (clinics ?? []) : undefined}
          onChange={set}
          onClear={() => {
            onSearch({})
          }}
        />
        <div ref={rowsRef} className="table-wrap" aria-busy={list.isFetching}>
          {list.isError ? (
            <div className="empty" role="alert">
              <Bug aria-hidden />
              <div>Couldn&apos;t load the errors.</div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  void list.refetch()
                }}
              >
                Retry
              </button>
            </div>
          ) : list.isPending ? (
            <div className="empty" role="status">
              <span className="spinner" aria-hidden />
              <div>Loading errors…</div>
            </div>
          ) : (
            <>
              {items.length > 0 && (
                <ErrorTable
                  rows={items}
                  timeZone={timeZone}
                  showClinic={adapter.crossClinic}
                  onOpen={(id) => {
                    onSearch({ ...search, error: id })
                  }}
                />
              )}
              {items.length === 0 && (
                <div className="empty">
                  <Bug aria-hidden />
                  <div>No errors match these filters</div>
                </div>
              )}
            </>
          )}
        </div>
        {!list.isError && (
          <Pager
            page={page?.page ?? search.page ?? 1}
            pageSize={page?.pageSize ?? PAGE_SIZE}
            total={page?.total ?? 0}
            noun="errors · newest first"
            onPage={(n) => {
              const next: ErrorsSearch = { ...search }
              delete next.error
              if (n > 1) next.page = n
              else delete next.page
              onSearch(next)
            }}
          />
        )}
      </div>

      {search.error && (
        <ErrorDrawer
          adapter={adapter}
          errorId={search.error}
          timeZone={timeZone}
          renderClinicLink={adapter.crossClinic ? renderClinicLink : undefined}
          onClose={() => {
            const next = { ...search }
            delete next.error
            onSearch(next, { replace: true })
          }}
        />
      )}
    </>
  )
}
